import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';
import { claimMailtoHref, safeWebHref } from '@/lib/utils/claim.utils';

export interface ClaimActionsProps {
  /** Where Claim Offer opens (http/https), or null. */
  claimLinkUrl: string | null | undefined;
  /** Where Contact Us opens the member's mail app, or null. The address itself is never shown (FR-117). */
  claimEmail: string | null | undefined;
  /** Called when the member activates Claim Offer, after the browser has begun the navigation. Not called in `preview` mode. */
  onClaim?: () => void;
  /** Called when the member activates Contact Us, after the browser has begun the navigation. Not called in `preview` mode. */
  onContact?: () => void;
  /** Renders the same layout non-interactively, for the partner's live preview of a card. */
  preview?: boolean;
}

/** Compact brand-yellow button padding shared by both actions (`!` beats Button's own padding). `whitespace-nowrap` keeps a label on one line however tight the card gets. */
const ACTION_CLASS = 'px-4! py-1.5! text-xs whitespace-nowrap';
/**
 * Contact Us collapses to its icon when the CARD is narrow, not the viewport: the same
 * viewport gives a card anywhere from ~300 to ~400 px (two or three grid columns), and
 * three actions only fit one row above ~21rem. The query reads the nearest `@container`
 * ancestor (the card's action row); a surface without one, such as the partner's live
 * preview, keeps the full label.
 */
const CONTACT_CLASS =
  'inline-flex items-center justify-center gap-1.5 px-4! py-1.5! text-xs whitespace-nowrap @max-[21rem]:px-2.5!';
const CONTACT_LABEL_CLASS = '@max-[21rem]:hidden';
const PREVIEW_CLASS = 'cursor-default!';

/**
 * The claim actions on an offer (FR-114, FR-117), shared by the member-facing card
 * and the partner's live preview so the two can never drift.
 *
 * - **Claim Offer** (brand yellow): only when a usable Claim Link exists; opens it in a new tab.
 * - **Contact Us** (brand yellow, email icon): only when a usable Email exists; opens the
 *   member's mail app addressed to it. The raw address is never rendered as text.
 * - neither usable: nothing is rendered.
 *
 * Both are real anchors, so the browser performs the navigation itself (no popup
 * blocking); `onClaim` / `onContact` only report the click afterwards. They stay on
 * one horizontal row at every width (`flex-nowrap`); when the card is narrow Contact Us
 * shows only its icon, with the same accessible name.
 */
export function ClaimActions({ claimLinkUrl, claimEmail, onClaim, onContact, preview = false }: ClaimActionsProps) {
  const linkHref = safeWebHref(claimLinkUrl);
  const mailHref = claimMailtoHref(claimEmail);
  if (!linkHref && !mailHref) return null;

  const previewClass = preview ? ` ${PREVIEW_CLASS}` : '';
  const contactLabel = (
    <>
      <Mail aria-hidden="true" className="size-4 shrink-0" />
      <span className={CONTACT_LABEL_CLASS}>{COMMON_STRINGS.CONTACT_US}</span>
    </>
  );

  return (
    <div className="flex flex-nowrap items-center gap-2">
      {linkHref && (
        <Button asChild variant="yellow" className={`${ACTION_CLASS}${previewClass}`}>
          {preview ? (
            <span>{COMMON_STRINGS.CLAIM_OFFER}</span>
          ) : (
            <a href={linkHref} target="_blank" rel="noopener noreferrer" onClick={() => onClaim?.()}>
              {COMMON_STRINGS.CLAIM_OFFER}
            </a>
          )}
        </Button>
      )}
      {mailHref && (
        <Button asChild variant="yellow" className={`${CONTACT_CLASS}${previewClass}`}>
          {preview ? (
            <span aria-label={COMMON_STRINGS.CONTACT_US} role="img">
              {contactLabel}
            </span>
          ) : (
            <a href={mailHref} aria-label={COMMON_STRINGS.CONTACT_US} onClick={() => onContact?.()}>
              {contactLabel}
            </a>
          )}
        </Button>
      )}
    </div>
  );
}
